(() => {
  "use strict";

  const SELECTORS = Object.freeze({
    root: "[data-faq-categories]",
    category: "details[data-faq-category]",
    toggle: "[data-faq-toggle]",
    panel: "[data-faq-panel]",
    anchor: "[data-faq-anchor]",
  });

  const ATTRS = Object.freeze({
    animating: "data-faq-animating",
  });

  const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function setExpanded(toggleEl, expanded) {
    if (!toggleEl) return;
    toggleEl.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  function isAnimating(el) {
    return el?.hasAttribute(ATTRS.animating);
  }

  function setAnimating(el, on) {
    if (!el) return;
    if (on) el.setAttribute(ATTRS.animating, "");
    else el.removeAttribute(ATTRS.animating);
  }

  function isOpen(details) {
    const panel = details.querySelector(SELECTORS.panel);
    return panel ? !panel.hidden : false;
  }

  function openPanel(panel) {
    if (!panel || isAnimating(panel)) return;
    if (!panel.hidden && panel.classList.contains("is-open")) return;

    // Reduced motion: no height animation
    if (prefersReducedMotion()) {
      panel.hidden = false;
      panel.classList.add("is-open");
      panel.style.height = "auto";
      return;
    }

    setAnimating(panel, true);

    panel.hidden = false;
    panel.classList.add("is-open");

    panel.style.height = "0px";
    panel.offsetHeight;

    const target = panel.scrollHeight;
    panel.style.height = `${target}px`;

    const done = (e) => {
      if (e.propertyName !== "height") return;
      panel.style.height = "auto";
      setAnimating(panel, false);
      panel.removeEventListener("transitionend", done);
    };

    panel.addEventListener("transitionend", done);
  }

  function closePanel(panel, onAfterClose) {
    if (!panel || isAnimating(panel)) return;
    if (panel.hidden) return;

    // Reduced motion: no height animation
    if (prefersReducedMotion()) {
      panel.classList.remove("is-open");
      panel.hidden = true;
      panel.style.height = "0px";
      if (typeof onAfterClose === "function") onAfterClose();
      return;
    }

    setAnimating(panel, true);

    const start = panel.scrollHeight;
    panel.style.height = `${start}px`;
    panel.offsetHeight;

    panel.style.height = "0px";
    panel.classList.remove("is-open");

    const done = (e) => {
      if (e.propertyName !== "height") return;

      panel.hidden = true;
      panel.style.height = "0px";
      setAnimating(panel, false);

      panel.removeEventListener("transitionend", done);
      if (typeof onAfterClose === "function") onAfterClose();
    };

    panel.addEventListener("transitionend", done);
  }

  function openDetails(details) {
    const toggle = details.querySelector(SELECTORS.toggle);
    const panel = details.querySelector(SELECTORS.panel);
    if (!toggle || !panel) return;

    if (!details.open) details.open = true;

    setExpanded(toggle, true);
    openPanel(panel);
  }

  function closeDetails(details) {
    const toggle = details.querySelector(SELECTORS.toggle);
    const panel = details.querySelector(SELECTORS.panel);
    if (!toggle || !panel) return;

    setExpanded(toggle, false);

    closePanel(panel, () => {
      details.open = false;
    });
  }

  function openAndScrollToAnchor(root, anchorEl) {
    const details = anchorEl.closest(SELECTORS.category);
    if (!details) return;

    openDetails(details);

    const panel = details.querySelector(SELECTORS.panel);
    if (panel && isAnimating(panel)) {
      panel.addEventListener(
        "transitionend",
        (e) => {
          if (e.propertyName !== "height") return;
          anchorEl.scrollIntoView({ block: "start", behavior: "smooth" });
        },
        { once: true }
      );
      return;
    }

    requestAnimationFrame(() => {
      anchorEl.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }

  function init(root) {
    const detailsList = Array.from(root.querySelectorAll(SELECTORS.category));
    if (!detailsList.length) return;

    detailsList.forEach((details) => {
      const toggle = details.querySelector(SELECTORS.toggle);
      const panel = details.querySelector(SELECTORS.panel);
      if (!toggle || !panel) return;

      details.open = false;
      setExpanded(toggle, false);

      panel.hidden = true;
      panel.classList.remove("is-open");
      panel.style.height = "0px";
      panel.removeAttribute(ATTRS.animating);
    });

    root.addEventListener("click", (e) => {
      const toggle = e.target.closest(SELECTORS.toggle);
      if (!toggle) return;

      const details = toggle.closest(SELECTORS.category);
      if (!details) return;

      // Prevent native <summary> toggle
      e.preventDefault();

      const panel = details.querySelector(SELECTORS.panel);
      if (panel && isAnimating(panel)) return;

      if (isOpen(details)) closeDetails(details);
      else openDetails(details);
    });

    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const anchor = root.querySelector(hash);
      if (anchor && anchor.matches(SELECTORS.anchor)) {
        openAndScrollToAnchor(root, anchor);
      }
    }

    window.addEventListener("hashchange", () => {
      const nextHash = window.location.hash;
      if (!nextHash || nextHash.length <= 1) return;

      const anchor = root.querySelector(nextHash);
      if (anchor && anchor.matches(SELECTORS.anchor)) {
        openAndScrollToAnchor(root, anchor);
      }
    });
  }

  function boot() {
    document.querySelectorAll(SELECTORS.root).forEach(init);
  }

  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target;
    if (!section) return;
    section.querySelectorAll(SELECTORS.root).forEach(init);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
